import { useEffect, useMemo } from 'react';

import { EMPTY_OBJ } from '../../constants';
import { Pagination, Sorting } from '../../types';
import { createFilterParams, createSortFilter } from '../../utils/queries';
import { useEventHandler } from '../useEventHandler';
import { usePaginationState } from '../usePaginationState';
import { useSortingState } from '../useSortingState';

type TableProps = {
  sorting: Sorting;
  pagination: Pagination;
  onSortingChange: (sorting: Sorting) => void;
  onPaginationChange: (pagination: Pagination) => void;
};

type UseTableParamsReturn = {
  tableProps: TableProps;
  searchParams: Record<string, string | number | boolean>;
};

export const useTableParams = (
  defaultSorting: Sorting,
  defaultPerPage: number = 10,
  additionalParams: Record<
    string,
    string | number | boolean | undefined
  > = EMPTY_OBJ,
): UseTableParamsReturn => {
  const [sorting, setSorting] = useSortingState(
    defaultSorting.column,
    defaultSorting.direction,
  );
  const [pagination, setPagination] = usePaginationState(defaultPerPage);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [additionalParams]);

  const onSortingChange = useEventHandler((newSorting: Sorting): void => {
    setSorting(newSorting);
    setPagination((prev) => ({ ...prev, page: 1 }));
  });

  const onPaginationChange = useEventHandler(
    (newPagination: Pagination): void => {
      setPagination(newPagination);
    },
  );

  const searchParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.perPage,
      ...createFilterParams(createSortFilter(sorting)),
      ...additionalParams,
    }),
    [pagination, sorting, additionalParams],
  );

  const tableProps: TableProps = useMemo(
    () => ({
      sorting,
      pagination,
      onSortingChange,
      onPaginationChange,
    }),
    [sorting, pagination, onSortingChange, onPaginationChange],
  );

  return {
    tableProps,
    searchParams,
  };
};
